// ProcessingPanel - what the documentation pipeline is doing with the
// uploaded documents (utils/glance/pipeline): one card per batch, each stage
// with its status, progress, notes and failures, and what a person should
// review. Polls while a batch is queued or running.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Progress, Skeleton, Tag, Tooltip, message } from "antd";
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined, MinusCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import ReconstructPanel from "./ReconstructPanel";
import { FollowUpNote } from "../review/DocumentationReview";
import ExpectedDocuments from "./ExpectedDocuments";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const STATUS_TAG = { queued: "default", running: "processing", done: "success", partial: "warning", failed: "error" };
const STATUS_TEXT = { queued: "Waiting", running: "Processing", done: "Done", partial: "Done, with problems", failed: "Failed" };
const TRIGGER = { upload: "Upload", manual: "Re-run", "set-import": "Drawing set" };

// The overlay is served only to the owner side, so it is fetched with the
// session's token and shown from a blob rather than linked directly.
export async function openOverlay(projectId, runId, file) {
  const tab = window.open("", "_blank");
  try {
    const r = await fetch(`${url}/projects/${projectId}/glance/pipeline/${runId}/trace/${encodeURIComponent(file)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).err || `HTTP ${r.status}`);
    const blob = await r.blob();
    const src = URL.createObjectURL(blob);
    if (tab) tab.location.href = src; else window.location.href = src;
  } catch (e) {
    if (tab) tab.close();
    message.error(e.message || "Could not open the trace");
  }
}

function StageIcon({ status }) {
  if (status === "done") return <CheckCircleFilled style={{ color: "var(--done)" }} />;
  if (status === "failed") return <CloseCircleFilled style={{ color: "var(--block)" }} />;
  if (status === "running") return <LoadingOutlined />;
  return <MinusCircleOutlined style={{ color: "var(--line)" }} />;
}

export default function ProcessingPanel({ projectId, onBuildingData }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${url}/projects/${projectId}/glance/pipeline`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then((b) => { setData(b); setError(""); })
      .catch((e) => setError(e.message));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const active = data && data.runs.some((r) => r.status === "queued" || r.status === "running");
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [active, load]);

  const rerun = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${url}/projects/${projectId}/glance/pipeline`, { method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: "{}" });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      message.success(b.merged ? "Added to the batch that is waiting" : `Processing ${b.documents} documents`);
      load();
    } catch (e) { message.error(e.message); }
    setBusy(false);
  };

  if (error && !data) return <div className="g-empty">{error} <Button size="small" onClick={load}>Retry</Button></div>;
  if (!data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <section>
      <div className="g-viewhead">
        <div>
          <h2>Processing</h2>
          <p className="g-note">
            Every upload is read, named and filed, lined up on the building grid, and turned into building data and views: nothing to type.
            Each batch shows where it is; anything the rules could not settle is listed for review. New documents and review decisions
            are gathered for a few minutes and applied in one update; what changes the levels, grid or zones is proposed for review first.
          </p>
          <FollowUpNote projectId={projectId} />
        </div>
        <span className="g-actions">
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
          {data.canAdd && (
            <Tooltip title="Run every document on this project through the pipeline again. Files already read are not read twice.">
              <Button size="small" loading={busy} onClick={rerun}>Process all documents again</Button>
            </Tooltip>
          )}
        </span>
      </div>
      <ReconstructPanel projectId={projectId} canEdit={!!data.canEdit} />
      {!data.runs.length && <Empty description="Nothing processed yet. Upload documents to start." />}
      {data.runs.map((run) => {
        const total = run.stages.length;
        const finished = run.stages.filter((s) => s.status === "done" || s.status === "skipped").length;
        const pending = (run.review || []).filter((r) => r.review === "pending");
        return (
          <div key={run._id} className="g-run-card">
            <div className="g-viewhead">
              <div>
                <b>{TRIGGER[run.trigger && run.trigger.kind] || "Batch"}: {run.docCount} document{run.docCount === 1 ? "" : "s"}</b>
                <span className="g-tsub"> {new Date(run.createdAt).toLocaleString()}{run.createdByName ? `, ${run.createdByName}` : ""}</span>
              </div>
              <span>
                <Tag color={STATUS_TAG[run.status]}>{STATUS_TEXT[run.status] || run.status}</Tag>
                {run.manifestVersion && (
                  <Button size="small" type="link" onClick={() => onBuildingData && onBuildingData(run.manifestVersion)}>Building data version {run.manifestVersion}</Button>
                )}
              </span>
            </div>
            <Progress percent={Math.round((finished / total) * 100)} size="small" status={run.status === "failed" ? "exception" : run.status === "running" ? "active" : undefined} />
            {run.error && <div className="g-callout">{run.error}</div>}
            <table>
              <tbody>
                {run.stages.map((s) => (
                  <tr key={s.key}>
                    <td style={{ width: 28 }}><StageIcon status={s.status} /></td>
                    <td style={{ width: 260 }}>{s.label}</td>
                    <td style={{ width: 90 }} className="g-num">{s.total ? `${s.done} / ${s.total}` : ""}</td>
                    <td>
                      {(s.notes || []).slice(0, 4).map((n) => <div key={n} className="g-tsub">{n}</div>)}
                      {(s.failures || []).slice(0, 6).map((n) => <div key={n} style={{ color: "var(--block)", fontSize: 13 }}>{n}</div>)}
                      {(s.failures || []).length > 6 && <div className="g-tsub">and {s.failures.length - 6} more</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pending.length > 0 && (
              // The review itself lives in the Review Center (owner, 2026-09-18):
              // this page only says how much is waiting and where.
              <p className="g-tsub" style={{ marginTop: 8 }}>
                {pending.length} item{pending.length === 1 ? "" : "s"} from this run waiting for review.{" "}
                <a href={`/approval-queue?projectId=${projectId}&tab=documentation`}>Review them in the Review Center</a>
              </p>            )}
          </div>
        );
      })}
      <ExpectedDocuments projectId={projectId} />
    </section>
  );
}
